import { Ban, Play, Star } from 'lucide-react';
import * as React from 'react';

import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// DataCardBadge
const dataBadgeVariants = {
  default: 'border-transparent bg-muted text-muted-foreground',
  sky: 'border-transparent bg-sky-100 text-sky-900 dark:bg-sky-950/80 dark:text-sky-100',
  cyan: 'border-transparent bg-cyan-100 text-cyan-900 dark:bg-cyan-950/80 dark:text-cyan-100',
  orange:
    'border-transparent bg-orange-100 text-orange-900 dark:bg-orange-950/80 dark:text-orange-100',
  blue: 'border-transparent bg-blue-100 text-blue-900 dark:bg-blue-950/80 dark:text-blue-100',
  emerald:
    'border-transparent bg-emerald-100 text-emerald-900 dark:bg-emerald-950/80 dark:text-emerald-100',
  fuchsia:
    'border-transparent bg-fuchsia-100 text-fuchsia-900 dark:bg-fuchsia-950/80 dark:text-fuchsia-100',
  slate: 'border-transparent bg-slate-200 text-slate-900 dark:bg-slate-800 dark:text-slate-100',
  violet:
    'border-transparent bg-violet-100 text-violet-900 dark:bg-violet-950/80 dark:text-violet-100',
  rose: 'border-transparent bg-rose-100/95 text-rose-900 dark:bg-rose-950/90 dark:text-rose-100',
  teal: 'border-transparent bg-[#E8F5FB] text-[#316B78] dark:bg-[#1a3a41] dark:text-[#a8d8e4]',
  brown: 'border-transparent bg-amber-100 text-amber-900 dark:bg-amber-950/80 dark:text-amber-100',
} as const;

type DataBadgeVariant = keyof typeof dataBadgeVariants;

function getDataBadgeClassName(color: DataBadgeVariant): string {
  return dataBadgeVariants[color];
}

function formatBadgeText(text: string): string {
  const spaced = text.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

type DataBadgeProps = React.ComponentProps<typeof Badge> & {
  color?: DataBadgeVariant;
};

function DataCardBadge({ className, color = 'default', ...props }: DataBadgeProps) {
  return (
    <Badge
      className={cn(
        'border h-auto px-1.5 py-0.5 xs:px-2 text-data-card-xs font-semibold shadow-sm',
        getDataBadgeClassName(color),
        className,
      )}
      {...props}
    />
  );
}

// DataCardContent
function DataCardContent({ className, ...props }: React.ComponentProps<typeof CardContent>) {
  return (
    <CardContent
      className={cn('group-data-[size=sm]/card:px-3 p-4 flex flex-col gap-2 flex-1', className)}
      {...props}
    />
  );
}

type BookmarkIconVariant = 'star' | 'prohibit';

type DataCardProps = React.ComponentProps<typeof Card> & {
  imageSrc?: string;
  imageAlt: string;
  imageFallbackSrc?: string;
  imageFallbackElement?: React.ReactNode;
  href?: string;
  showBookmarkIcon?: boolean;
  isBookMarked?: boolean;
  bookmarkIconVariant?: BookmarkIconVariant;
  playable?: boolean;
  badgeText?: string;
  badgeVariant?: DataBadgeVariant;
  onBookmarkClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  bookmarkLabel?: string;
};

function DataCard({
  className,
  href,
  imageSrc,
  imageAlt,
  imageFallbackSrc,
  imageFallbackElement,
  showBookmarkIcon = false,
  isBookMarked = false,
  bookmarkIconVariant = 'star',
  playable = false,
  badgeText,
  badgeVariant = 'default',
  onBookmarkClick,
  bookmarkLabel,
  children,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  ...props
}: DataCardProps) {
  const [imageErrored, setImageErrored] = React.useState(false);

  // Reset the error flag when the source changes (list re-render / client nav)
  // so a new URL gets a fresh attempt instead of staying stuck on the fallback.
  React.useEffect(() => setImageErrored(false), [imageSrc]);

  const useFallbackElement = (!imageSrc || imageErrored) && imageFallbackElement != null;
  const imgSrc = imageErrored ? imageFallbackSrc : imageSrc || imageFallbackSrc;

  const isClickable = href != null || onClick != null;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || href == null) return;
    window.location.href = href;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !isClickable) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    if (href != null) {
      window.location.href = href;
      return;
    }

    event.currentTarget.click();
  };

  return (
    <Card
      className={cn(
        'w-full gap-0 overflow-hidden rounded-2xl border border-card-border py-0 shadow-sm ring-1 ring-foreground/10 transition-shadow',
        isClickable &&
          'cursor-pointer hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      onClick={isClickable ? handleClick : onClick}
      onKeyDown={isClickable ? handleKeyDown : onKeyDown}
      role={isClickable ? (role ?? (href != null ? 'link' : 'button')) : role}
      tabIndex={isClickable ? (tabIndex ?? 0) : tabIndex}
      {...props}
    >
      <AspectRatio ratio={16 / 9} className="w-full shrink-0 overflow-hidden bg-muted">
        {useFallbackElement ? (
          <div className="absolute inset-0">{imageFallbackElement}</div>
        ) : (
          <img
            src={imgSrc}
            alt={imageAlt}
            className={cn('absolute inset-0 size-full object-fill')}
            onError={() => setImageErrored(true)}
          />
        )}

        {playable ? (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            aria-hidden
          >
            <div className="flex size-9 items-center justify-center rounded-full border border-white/70 bg-data-card-play-overlay text-data-card-play-icon shadow-sm backdrop-blur-[2px] xs:size-10 sm:size-11 lg:size-12 lg:border-2 dark:bg-data-card-play-overlay-dark">
              <Play
                className="size-4 translate-x-0.5 fill-data-card-play-icon text-data-card-play-icon filter-[drop-shadow(0_1px_2px_rgba(0,0,0,0.65))] xs:size-4.5 sm:size-5 lg:size-5.5"
                strokeWidth={1.5}
              />
            </div>
          </div>
        ) : null}

        {badgeText ? (
          <div className="absolute top-3 left-3 z-10">
            <DataCardBadge color={badgeVariant}>{formatBadgeText(badgeText)}</DataCardBadge>
          </div>
        ) : null}

        {showBookmarkIcon ? (
          <div
            className="absolute top-2 right-2.5 z-10"
            onClick={(event) => event.stopPropagation()}
          >
            <TooltipProvider delay={200}>
              <Tooltip>
                <TooltipTrigger
                  type="button"
                  aria-label={bookmarkLabel ?? (isBookMarked ? 'Remove bookmark' : 'Add bookmark')}
                  onClick={onBookmarkClick}
                  className={cn(
                    'flex size-7.5 items-center justify-center rounded-full border-0 bg-data-card-bookmark-button-bg transition-colors hover:bg-data-card-bookmark-button-bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  )}
                >
                  {bookmarkIconVariant === 'prohibit' ? (
                    <Ban className="size-3.5 text-destructive" strokeWidth={1.5} aria-hidden />
                  ) : (
                    <Star
                      className={cn(
                        'size-3.5',
                        isBookMarked
                          ? 'fill-data-card-bookmark-icon-active text-data-card-bookmark-icon-active'
                          : 'fill-transparent text-data-card-bookmark-icon-inactive',
                      )}
                      strokeWidth={1.5}
                      aria-hidden
                    />
                  )}
                </TooltipTrigger>
                <TooltipContent side="left">
                  {bookmarkLabel ?? (isBookMarked ? 'Remove bookmark' : 'Add bookmark')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : null}
      </AspectRatio>

      {children}
    </Card>
  );
}

export { DataCard, DataCardBadge, DataCardContent, getDataBadgeClassName, type DataBadgeVariant };
